import React from 'react';
import {
  Row, Col, Button, Form, OverlayTrigger, Tooltip,
} from 'react-bootstrap';
import { useForm, UseFormReturn, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import api from 'api';
import { BurncenterAccessLevelEnum } from 'utils/enums';

type AdminValues = {
  burnCenterAccessLevel: string | null;
  sigmaTeam: string | null;
  jwtSigmaWorkspaceLandingLink: string | null;
};

type Props = {
  id: string;
  canEdit: boolean;
  values: Readonly<Partial<AdminValues>>;
  onSaved?: () => void;
};

const emptyToNull = (v: unknown) => {
  if (typeof v === 'string') {
    const t = v.trim();
    return t === '' ? null : t;
  }
  return v;
};

const schema: yup.ObjectSchema<AdminValues> = yup
  .object({
    burnCenterAccessLevel: yup
      .string()
      .transform(emptyToNull)
      .oneOf([...Object.values(BurncenterAccessLevelEnum), null] as any)
      .nullable()
      .defined(),
    sigmaTeam: yup.string().transform(emptyToNull).nullable().defined(),
    jwtSigmaWorkspaceLandingLink: yup
      .string()
      .transform(emptyToNull)
      .url('Invalid URL')
      .nullable()
      .defined(),
  })
  .required();

const normalizeDefaults = (v: Partial<AdminValues>): AdminValues => ({
  burnCenterAccessLevel: v.burnCenterAccessLevel ?? null,
  sigmaTeam: v.sigmaTeam ?? null,
  jwtSigmaWorkspaceLandingLink: v.jwtSigmaWorkspaceLandingLink ?? null,
});

const hasSigmaTeam = (sigmaTeam: string | null): boolean => (sigmaTeam?.trim() ?? '') !== '';

type FieldProps = {
  label: string;
  name: keyof AdminValues;
  type?: string;
  form: Pick<UseFormReturn<AdminValues>, 'register' | 'formState'>;
  editing: boolean;
  locked?: boolean;
  values: AdminValues;
  disabled?: boolean;
  autoComplete?: string;
  truncate?: boolean;
};

const Field: React.FC<FieldProps> = ({
  label,
  name,
  type = 'text',
  form,
  editing,
  locked = false,
  values,
  disabled,
  autoComplete,
  truncate = false,
}) => {
  const error = form.formState.errors[name]?.message as string | undefined;
  const val = values[name];

  if (!editing) {
    let displayValue: React.ReactNode;
    if (val == null) {
      displayValue = <span className="text-muted fst-italic">n/a</span>;
    } else if (truncate) {
      const text = String(val);
      displayValue = (
        <OverlayTrigger
          placement="top"
          trigger={['hover']}
          overlay={(
            <Tooltip id={`field-${name}`} className="text-break">
              {text}
            </Tooltip>
          )}
        >
          <span className="d-block text-truncate min-w-0">
            {text}
          </span>
        </OverlayTrigger>
      );
    } else {
      displayValue = val;
    }

    return (
      <div className={`mb-3${truncate ? ' min-w-0' : ''}`}>
        <h6 className="fw-bold">{label}</h6>
        <p className={`mb-0${truncate ? ' min-w-0' : ''}`}>
          {displayValue}
        </p>
      </div>
    );
  }

  if (locked) {
    return (
      <Form.Group className="mb-3">
        <Form.Label className="fw-bold">{label}</Form.Label>
        <Form.Control
          plaintext
          readOnly
          value={val ?? ''}
          className={!val ? 'text-muted fst-italic' : undefined}
          placeholder={!val ? 'n/a' : undefined}
        />
      </Form.Group>
    );
  }

  return (
    <Form.Group className="mb-3">
      <Form.Label className="fw-bold">{label}</Form.Label>
      <Form.Control
        type={type}
        {...form.register(name)}
        isInvalid={!!error}
        disabled={disabled}
        autoComplete={autoComplete}
      />
      <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
    </Form.Group>
  );
};

const AdminTab: React.FC<Props> = ({
  id,
  canEdit,
  values: incoming,
  onSaved,
}) => {
  const [editing, setEditing] = React.useState(false);
  const defaults = React.useMemo(() => normalizeDefaults(incoming), [incoming]);
  const sigmaTeamLocked = hasSigmaTeam(defaults.sigmaTeam);

  const form = useForm<AdminValues>({
    defaultValues: defaults,
    resolver: yupResolver(schema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const { isSubmitting, isDirty, errors } = form.formState;

  React.useEffect(() => {
    if (!editing) form.reset(normalizeDefaults(incoming));
  }, [incoming, editing, form]);

  const onSubmit = React.useCallback(
    async (data: AdminValues) => {
      const payload: AdminValues = sigmaTeamLocked
        ? { ...data, sigmaTeam: defaults.sigmaTeam }
        : data;
      await api.updateBurnCenterSetupById(id, payload);
      setEditing(false);
      onSaved?.();
    },
    [defaults.sigmaTeam, id, onSaved, sigmaTeamLocked],
  );

  const handleCancel = React.useCallback(() => {
    form.reset(defaults);
    setEditing(false);
  }, [defaults, form]);

  return (
    <div>
      <div className="d-flex justify-content-end align-items-end mb-3">
        {canEdit && !editing && (
          <Button variant="secondary" onClick={() => setEditing(true)}>
            Edit
          </Button>
        )}
        {canEdit && editing && (
          <div>
            <Button
              variant="secondary"
              className="me-2"
              type="submit"
              form="admin-form"
              disabled={isSubmitting || !isDirty}
            >
              {isSubmitting ? 'Updating…' : 'Update'}
            </Button>
            <Button
              variant="outline-secondary"
              disabled={isSubmitting}
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>

      <Form id="admin-form" onSubmit={form.handleSubmit(onSubmit)} noValidate>
        <Row className="g-3 align-items-start">
          <Col xs={12} md={6} lg={4}>
            {!editing ? (
              <div className="mb-3">
                <h6 className="fw-bold">Access Level</h6>
                <p className="mb-0">
                  {defaults.burnCenterAccessLevel ?? <span className="text-muted fst-italic">n/a</span>}
                </p>
              </div>
            ) : (
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Access Level</Form.Label>
                <Controller
                  control={form.control}
                  name="burnCenterAccessLevel"
                  render={({ field }) => (
                    <Form.Select
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value || null)}
                      disabled={isSubmitting}
                      isInvalid={!!errors.burnCenterAccessLevel}
                    >
                      <option value="">Select access level</option>
                      {Object.values(BurncenterAccessLevelEnum).map((level) => (
                        <option key={level} value={level}>
                          {level}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                />
                <Form.Control.Feedback type="invalid" className="d-block">
                  {errors.burnCenterAccessLevel?.message as string}
                </Form.Control.Feedback>
              </Form.Group>
            )}
          </Col>
          <Col xs={12} md={6} lg={4}>
            <Field
              label="Sigma team"
              name="sigmaTeam"
              form={form}
              editing={editing}
              locked={sigmaTeamLocked}
              values={defaults}
              disabled={isSubmitting}
            />
          </Col>
          <Col xs={12} md={6} lg={4} className="min-w-0">
            <Field
              label="JWT Sigma workspace landing link"
              name="jwtSigmaWorkspaceLandingLink"
              type="url"
              form={form}
              editing={editing}
              values={defaults}
              disabled={isSubmitting}
              autoComplete="url"
              truncate
            />
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default AdminTab;
